/**
 * Helper functions for assessment-related components
 */

/**
 * Format answer for display
 */
export const formatAnswer = (
  answer: string | Record<string, any> | null | undefined
): string => {
  if (!answer) return "Candidate did not provide an answer.";
  if (typeof answer === "string") return answer;
  return JSON.stringify(answer, null, 2);
};

/**
 * Check if answer is a JSON object
 */
export const isJsonAnswer = (
  answer: string | Record<string, any> | null | undefined
): boolean => {
  return answer !== null && answer !== undefined && typeof answer === "object";
};

/**
 * Check if answer has meaningful content
 * Returns false if answer is null, undefined, empty string, or object with all null/undefined values
 */
export const hasAnswerContent = (
  answer: string | number | boolean | Record<string, any> | null | undefined
): boolean => {
  // Explicitly check for null and undefined
  if (answer === null || answer === undefined) return false;

  // Numbers (including 0) and booleans (including false) are valid answers
  if (typeof answer === "number" || typeof answer === "boolean") {
    return true;
  }

  if (typeof answer === "string") {
    return answer.trim().length > 0;
  }

  // For objects, check if at least one value is non-null/non-undefined
  if (typeof answer === "object") {
    return Object.values(answer).some(
      (value) => value !== null && value !== undefined
    );
  }

  return false;
};

/**
 * Get color for session state chip
 */
export const getSessionStateColor = (
  state: string
): "success" | "primary" | "warning" | "default" => {
  switch (state) {
    case "ready":
      return "success";
    case "active":
      return "primary";
    case "processing":
      return "warning";
    case "created":
    default:
      return "default";
  }
};

/**
 * Get icon component for session state
 */
export const getSessionStateIcon = (state: string): React.ReactNode => {
  // Note: This function returns the icon name/identifier
  // The actual icon component should be imported in the consuming component
  switch (state) {
    case "ready":
      return "CheckCircle";
    case "active":
      return "RadioButtonUnchecked";
    case "processing":
      return "HourglassEmpty";
    case "created":
    default:
      return "Schedule";
  }
};
